/**
 * Mistik Dream · Registro: o que vale para o grupo, e não para um personagem.
 *
 * <p>Dia, turno e localização saíram das fichas e vieram para cá. Eles nunca foram do
 * personagem: o time joga junto, e seis fichas guardando "turno 7" seriam seis verdades
 * sobre o mesmo relógio — que divergem no primeiro esquecimento.
 *
 * <p><b>Os dados são de doze faces</b>, um por jogador, na cor da conta dele. Quem rola é
 * o próprio jogador, e é a única escrita desta tela que não é do mestre — porque é a
 * única em que ele mexe apenas no que é dele.
 *
 * <p><b>A rolagem é do servidor.</b> Um sorteio no navegador é um sorteio que se repete
 * até gostar do número. Aqui ela é gravada, então sobrevive ao F5 e aparece para o grupo
 * — mas <b>não chega sozinha</b>: quem estiver com a tela aberta vê o dado do outro no
 * próximo carregamento, e não no instante do clique.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ErroApi } from '../../api/cliente';
import { useSessao } from '../../auth/SessaoContext';
import { TituloDaPagina } from '../../shell/TituloDaPagina';
import * as fichasApi from './fichasApi';
import * as registroApi from './registroApi';
import './fichas.css';

export function Registro() {
  const { usuario } = useSessao();
  const souMestre = Boolean(usuario?.mestre);
  const clienteDeConsultas = useQueryClient();

  const [erro, setErro] = useState('');
  const [rascunho, setRascunho] = useState<registroApi.Registro['conteudo'] | null>(null);

  const registro = useQuery({
    queryKey: registroApi.CHAVE_DO_REGISTRO,
    queryFn: registroApi.ler,
    staleTime: Infinity,
  });

  /*
   * A mesa vem das FICHAS, e não de uma lista de jogadores: quem tem personagem está na
   * mesa. Uma segunda lista de participantes divergiria da primeira no dia em que alguém
   * entrasse — e ninguém lembraria de atualizar as duas.
   */
  const fichas = useQuery({
    queryKey: fichasApi.CHAVE_DAS_FICHAS,
    queryFn: fichasApi.listar,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (registro.data && !rascunho) setRascunho(registro.data.conteudo ?? {});
  }, [registro.data, rascunho]);

  const gravacao = useMutation({
    mutationFn: (c: registroApi.Registro['conteudo']) =>
      registroApi.salvar(c, registro.data?.versao ?? 0),
    onSuccess: (novo) => {
      setRascunho(novo.conteudo ?? {});
      setErro('');
      void clienteDeConsultas.invalidateQueries({ queryKey: registroApi.CHAVE_DO_REGISTRO });
    },
    onError: (e) => setErro(e instanceof ErroApi ? e.message : 'Não consegui gravar.'),
  });

  const rolagem = useMutation({
    mutationFn: registroApi.rolar,
    onSuccess: () =>
      void clienteDeConsultas.invalidateQueries({ queryKey: registroApi.CHAVE_DO_REGISTRO }),
    onError: (e) => setErro(e instanceof ErroApi ? e.message : 'Não consegui rolar.'),
  });

  const r = rascunho ?? {};
  const rolagens = registro.data?.conteudo?.rolagens ?? {};
  const mudar = (mudanca: Partial<registroApi.Registro['conteudo']>) =>
    setRascunho((a) => ({ ...(a ?? {}), ...mudanca }));

  return (
    <div className="fi-md">
      <TituloDaPagina>
        O que vale para o grupo: em que dia e turno a mesa está, onde ela está, e os dados
        de cada um.
      </TituloDaPagina>

      <p className="fi-md-estado">
        {souMestre ? (
          <>
            Você mestra esta mesa: o <b>dia, o turno e a localização</b> são seus. O dado é
            de cada jogador.
          </>
        ) : (
          <>
            Quem marca dia, turno e localização é o <b>mestre</b>. O seu dado é seu — role
            quando ele pedir.
          </>
        )}
      </p>

      {erro && <p className="fi-md-erro">{erro}</p>}
      {registro.isLoading && <p className="fi-md-espera">Abrindo o registro…</p>}

      {registro.error && (
        <p className="fi-md-erro">
          {registro.error instanceof Error
            ? registro.error.message
            : 'Não foi possível abrir o registro.'}
        </p>
      )}

      {rascunho && (
        <>
          <h3 className="fd-divisor">Onde a mesa está</h3>
          <div className="rg-grade">
            <label className="fd-linha">
              <span>Dia</span>
              <input
                className="fd-numero"
                type="text"
                inputMode="numeric"
                value={String(r.dia ?? 1)}
                readOnly={!souMestre}
                onChange={(e) => mudar({ dia: soNumero(e.target.value) })}
              />
            </label>
            <label className="fd-linha">
              <span>Turno</span>
              <input
                className="fd-numero"
                type="text"
                inputMode="numeric"
                value={String(r.turno ?? 1)}
                readOnly={!souMestre}
                onChange={(e) => mudar({ turno: soNumero(e.target.value) })}
              />
            </label>
            <label className="fd-linha larga">
              <span>Localização</span>
              <input
                value={r.localizacao ?? ''}
                placeholder="Vila de Tau, na taverna"
                readOnly={!souMestre}
                onChange={(e) => mudar({ localizacao: e.target.value })}
              />
            </label>
          </div>

          <h3 className="fd-divisor">Anotações do grupo</h3>
          <textarea
            className="rg-notas"
            rows={5}
            value={r.notas ?? ''}
            placeholder="O que o grupo combinou, o que ficou pendente, o que o mestre quer lembrar…"
            readOnly={!souMestre}
            aria-label="Anotações do grupo"
            onChange={(e) => mudar({ notas: e.target.value })}
          />

          {souMestre && (
            <div className="fd-rodape solto">
              <button
                type="button"
                className="fd-gravar"
                onClick={() => gravacao.mutate(r)}
                disabled={gravacao.isPending}
              >
                {gravacao.isPending ? 'Gravando…' : 'Salvar registro'}
              </button>
              <span className="fd-pendencia">
                {registro.data?.atualizadoPor
                  ? `Última gravação por ${registro.data.atualizadoPor}.`
                  : 'Ninguém gravou ainda.'}
              </span>
            </div>
          )}
        </>
      )}

      <h3 className="fd-divisor">Os dados</h3>
      <p className="fd-explica solta">
        Um dado de doze faces por jogador, na cor da conta dele. Quem rola é o dono — e o
        resultado é sorteado no servidor, para valer para a mesa. Ele fica gravado, mas{' '}
        <b>não aparece sozinho</b> na tela dos outros: quem quiser ver o seu recarrega.
      </p>

      <div className="rg-dados">
        {fichas.data?.map((f) => {
          const minha = f.dono === usuario?.handle;
          const ultima = rolagens[f.dono];
          return (
            <div className="rg-dado" key={f.id} style={{ borderColor: f.corDoDono }}>
              <span className="rg-dado-quem" style={{ color: f.corDoDono }}>
                {f.nome}
              </span>
              <span className="rg-dado-dono">{f.nomeDoDono}</span>
              <b className="rg-dado-valor" style={{ color: f.corDoDono }}>
                {ultima ? ultima.valor : '—'}
              </b>
              {minha ? (
                <button
                  type="button"
                  className="rg-rolar"
                  style={{ borderColor: f.corDoDono, color: f.corDoDono }}
                  onClick={() => rolagem.mutate()}
                  disabled={rolagem.isPending}
                >
                  {rolagem.isPending ? 'Rolando…' : 'Rolar d12'}
                </button>
              ) : (
                <span className="rg-dado-quando">{ultima ? quando(ultima.em) : 'sem rolagem'}</span>
              )}
            </div>
          );
        })}
      </div>

      {fichas.data?.length === 0 && (
        <p className="fi-md-vazio">
          Ninguém criou personagem ainda — os dados aparecem quando a mesa tiver gente.
        </p>
      )}
    </div>
  );
}

const soNumero = (texto: string) => {
  const limpo = texto.replace(/\D/g, '');
  return limpo === '' ? 0 : Number(limpo);
};

/** A hora da rolagem no fuso de quem lê. O servidor manda UTC. */
function quando(iso: string): string {
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return '';
  return data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}
